import { StagesService } from '@/services';
import { OpenCallStage, ViewPermissions } from '@repo/api/open-calls/graphql';
import * as permifyConstants from '@repo/api/permify/constants';
import {
  createInitialState,
  InitialState,
  viewPermissionsStore,
} from '@repo/ui/state';
import { create } from 'zustand';
import { formatError } from '../error-handling';

export const initialState = {
  ...createInitialState([]),
  count: 0,
};

export interface StagesState extends InitialState<OpenCallStage[]> {
  count: number;
  changeCandidateCount: (counts: Record<string, number>) => void;
  fetchStages: (openCallId: string) => Promise<void>;
  createStage: (openCallId: string) => Promise<OpenCallStage | null>;
  renameStage: (data: {
    openCallId: string;
    stageId: string;
    newName: string;
  }) => Promise<OpenCallStage | null>;
  reset: () => void;
}

export const store = create<StagesState>((set, get) => ({
  data: initialState.data,
  loading: initialState.loading,
  error: initialState.error,
  count: initialState.count,

  fetchStages: async (openCallId: string) => {
    set((state) => ({
      ...state,
      data: initialState.data,
      error: null,
      loading: true,
      openCallId,
    }));
    try {
      const data = await StagesService.queryByOpenCallId(openCallId);
      // Populate permissions when stages are fetched
      const viewPermissions = data
        ?.flatMap((stage) => stage.viewPermissions)
        .filter(Boolean); // Filter out any undefined/null values

      viewPermissionsStore
        .getState()
        .setPermissions(viewPermissions as ViewPermissions[]);

      set((state) => ({ ...state, data }));
    } catch (error: unknown) {
      set((state) => ({
        ...state,
        error: formatError(error),
      }));
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },

  changeCandidateCount: (counts) => {
    set((state) => ({
      ...state,
      data: state.data.map((stage) => ({
        ...stage,
        count: Number(
          counts[stage.id] !== undefined ? counts[stage.id] : stage.count,
        ),
      })),
    }));
  },

  reset: () => {
    set((state) => ({
      ...state,
      data: initialState.data,
      count: initialState.count,
      loading: initialState.loading,
      error: initialState.error,
    }));
  },

  createStage: async (openCallId: string) => {
    set((state) => ({ ...state, loading: true }));
    try {
      const newStage = await StagesService.createStage(openCallId);
      let indexToCreateNewStage = get().data.findLastIndex(
        (stage) =>
          !Object.values(permifyConstants.stageNames).includes(stage.name),
      );
      if (indexToCreateNewStage === -1) {
        indexToCreateNewStage = get().data.findIndex(
          (stage) => stage.name === permifyConstants.stageNames.applicants,
        );
      }
      set((state) => ({
        ...state,
        data: [
          ...state.data.slice(0, indexToCreateNewStage + 1),
          newStage!,
          ...state.data.slice(indexToCreateNewStage + 1),
        ],
      }));
      return newStage;
    } catch (error: unknown) {
      set((state) => ({
        ...state,
        error: formatError(error),
      }));
      return null;
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
  renameStage: async ({ openCallId, stageId, newName }) => {
    set((state) => ({ ...state, loading: true }));
    try {
      const updatedStage = await StagesService.renameStage(
        openCallId,
        stageId,
        newName,
      );
      set((state) => ({
        ...state,
        error: null,
        data: state.data.map((stage) =>
          stage.id === updatedStage.id
            ? { ...updatedStage, count: stage.count }
            : stage,
        ),
      }));
      return updatedStage;
    } catch (error: unknown) {
      set((state) => ({
        ...state,
        error: formatError(error),
      }));
      return null;
    } finally {
      set((state) => ({
        ...state,
        loading: false,
      }));
    }
  },
}));
